import {createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import type {ReactNode} from 'react';
import {useBlocker} from 'react-router-dom';
import {createDraftNavigationGuard, preventUnsafeUnload} from './draftNavigationGuard.js';
import type {AppDraftGuard, DraftNavigationGuard} from './draftNavigationGuard.js';

const DraftGuardContext = createContext<DraftNavigationGuard | null>(null);
const routerChannel = {};

export function AppDraftGuardProvider({children}: {children: ReactNode}) {
  const [guard] = useState(createDraftNavigationGuard);
  const [unsafe, setUnsafe] = useState(false);
  const shouldBlock = useCallback(() => guard.isUnsafe(), [guard]);
  const blocker = useBlocker(shouldBlock);

  useLayoutEffect(() => {
    const update = () => setUnsafe(guard.isUnsafe());
    const unsubscribe = guard.subscribe(update);
    update();
    return unsubscribe;
  }, [guard]);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    void guard.request(blocker.proceed, {reject: blocker.reset, channel: routerChannel});
  }, [blocker, guard]);

  useLayoutEffect(() => {
    if (!unsafe) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => preventUnsafeUnload(guard, event);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [guard, unsafe]);

  return <DraftGuardContext.Provider value={guard}>{children}</DraftGuardContext.Provider>;
}

/** Callbacks are read from the latest render, including edits during an in-flight save. */
export function useAppDraftGuard(options: AppDraftGuard) {
  const guard = useContext(DraftGuardContext);
  if (!guard) throw new Error('A draft guard requires AppNavigationRoot.');
  const latest = useRef(options);
  latest.current = options;
  const ownerKey = options.ownerKey;
  useLayoutEffect(
    () =>
      guard.register({
        ownerKey,
        isUnsafeToLeave: () => latest.current.isUnsafeToLeave(),
        settleBeforeLeave: () => latest.current.settleBeforeLeave(),
      }),
    [guard, ownerKey],
  );
  useLayoutEffect(() => guard.refresh());
}

/** Header intents must be checked before they clear forms or change the current view. */
export function useAppGuardedNavigation() {
  const guard = useContext(DraftGuardContext);
  return useCallback(
    (action: () => void) => {
      if (guard) return guard.request(action);
      // Isolated chrome renders outside the application root have no drafts.
      action();
      return Promise.resolve(true);
    },
    [guard],
  );
}
